import {Component, OnDestroy, OnInit} from '@angular/core';
import {ApiFlowService} from "../../../../data/services/api/flow/api-flow.service";
import {IFlow, IFlowByStages, IFlowStatus} from "../../../../data/interfaces/iflow.interface";
import {ActivatedRoute, Route, Router} from "@angular/router";
import {ApiExpressLeagueService} from "../data/services/api/api-express-league.service";
import {AuthService} from "../../../../data/services/modules/auth/auth.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {LoaderService} from "../data/services/page/loader.service";
import {Message, MessageService} from "primeng/api";
import {catchError, concatMap, finalize, map, Observable, of, Subscription, timeout} from "rxjs";
import {VerifyInformationService} from "../data/services/page/verify-information.service";
import {
  faArrowRight,
  faCheck,
  faCircleExclamation,
  faCircleInfo,
  faClock,
  faCommentSms,
  faEnvelope,
  faFileSignature,
  faHourglassHalf,
  faIdCard,
  faPaperPlane,
  faPenToSquare,
  faRotateRight,
  faShieldHalved,
  faUser,
  faXmark,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import {ESTADOS_LX, EstadoLx} from "../../../../shared/ui/liga-ui";

// ---- Solo presentación (pantalla de entrada y estados finales) ----

// Aviso en línea de la pantalla de entrada: sale de los mismos mensajes que
// antes iban al toast (MessageService), sin tocar sendSMS() ni continue().
export interface IAvisoEntrada {
  tipo: 'exito' | 'error'
  titulo: string
  texto: string
}

export interface IPasoLineaFinal {
  titulo: string
  texto: string
  estado: EstadoLx
  // Texto del chip si el de ESTADOS_LX no aplica (p. ej. «En proceso» en el depósito).
  chip: string
}

export interface IEstadoFinal {
  // Palomita animada (true) o ilustración con halo (false).
  exito: boolean
  icono: IconDefinition
  chip: EstadoLx
  chipTexto: string
  titulo: string
  texto: string
  nota: string
  notaTipo: 'info' | 'exito'
  linea: IPasoLineaFinal[]
}

@Component({
  selector: 'app-verify-information',
  templateUrl: './verify-information.component.html',
  styleUrls: ['./verify-information.component.scss'],
  providers: [MessageService]
})
export class VerifyInformationComponent implements OnInit, OnDestroy {
  data!: IFlowByStages
  flowStatus: IFlowStatus | null = null
  titleSection = 'Tu solicitud'
  backRoute = '/'
  screenVerificationList = true
  currentScreenType = 'FORM'
  currentScreenData!: IFlow
  // Arranca en LOADING: hasta resolver si hay sesión/token, no se debe pintar el
  // formulario de registro (RFC) para luego cambiarlo por el flujo.
  stepProcess = 'LOADING'
  flujoCargado = false
  session = ''
  expressLeagueType = 'LOAN'
  entity!: FormGroup
  codeSent = false
  isPhoneVerified: boolean = false;
  timer = 120
  private timerInterval!: any;
  token = ''

  constructor(
    private flowService: ApiFlowService,
    private activatedRoute: ActivatedRoute,
    private expressLeagueService: ApiExpressLeagueService,
    private authService: AuthService,
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private messageService: MessageService,
    private router: Router,
    private verifyInformationService: VerifyInformationService
  ) {
    this.createForm()
  }

  createForm() {
    this.entity = this.fb.group({
      rfc: ['', [Validators.required, Validators.pattern(/^[A-Z]{4}[0-9]{2}[0-1][0-9][0-3][0-9][0-9A-Z]{3}$/)]],
      emailAddress: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_.+-]*[a-zA-Z][a-zA-Z0-9_.+-]*@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/)]],
      phoneNumber: ['',  [Validators.required, Validators.pattern((/^\d{10}$/))]],
      secondPhoneNumber: ['',  [Validators.pattern((/^\d{10}$/))]],
      code: ['']
    })
  }

  ngOnInit() {
    this.suscripcionAvisos = this.messageService.messageObserver.subscribe(m => this.mostrarAviso(m))
    this.getQueryParams()
  }

  ngOnDestroy() {
    this.suscripcionAvisos?.unsubscribe()
  }

  // ===================== Presentación =====================
  // Nada de aquí cambia llamadas, cuerpos ni orden: solo arma lo que se pinta.

  readonly estados = ESTADOS_LX
  readonly iconos = {
    usuario: faUser,
    sms: faCommentSms,
    correo: faEnvelope,
    rfc: faIdCard,
    error: faCircleExclamation,
    info: faCircleInfo,
    reloj: faClock,
    reenviar: faRotateRight,
    listo: faCheck,
    flecha: faArrowRight,
    seguro: faShieldHalved,
    cerrar: faXmark,
    editar: faPenToSquare
  }

  avisoEntrada: IAvisoEntrada | null = null
  // Con el código enviado, los datos se ven como resumen; «Cambiar mis datos»
  // los vuelve a mostrar (mismo formulario, mismos valores).
  editandoDatos = false
  intentoCodigo = false
  private suscripcionAvisos?: Subscription

  // Error en línea al salir del campo (o al intentar enviar).
  invalido(campo: string): boolean {
    const control = this.entity.get(campo)
    return !!control && control.invalid && control.touched
  }

  // RFC en mayúsculas y sin espacios ni guiones mientras se escribe.
  alEscribirRfc(evento: Event) {
    const input = evento.target as HTMLInputElement
    const limpio = (input.value ?? '').toUpperCase().replace(/[\s-]/g, '')
    if (limpio !== input.value) {
      const posicion = Math.min(input.selectionStart ?? limpio.length, limpio.length)
      this.entity.get('rfc')?.setValue(limpio)
      input.value = limpio
      try { input.setSelectionRange(posicion, posicion) } catch { /* sin cursor */ }
    }
  }

  alSalirCorreo() {
    const control = this.entity.get('emailAddress')
    const valor = String(control?.value ?? '')
    if (valor.trim() !== valor) control?.setValue(valor.trim())
  }

  // El código viene por SMS: se quitan espacios al pegarlo.
  alEscribirCodigo(evento: Event) {
    const input = evento.target as HTMLInputElement
    const limpio = (input.value ?? '').replace(/\s/g, '')
    if (limpio !== input.value) {
      this.entity.get('code')?.setValue(limpio)
      input.value = limpio
    }
  }

  get codigoVacio(): boolean {
    return String(this.entity.get('code')?.value ?? '').trim() === ''
  }

  get longitudRfc(): number {
    return String(this.entity.get('rfc')?.value ?? '').length
  }

  // «•• •••• 3312» para el texto del código enviado.
  get telefonoOculto(): string {
    const digitos = String(this.entity.get('phoneNumber')?.value ?? '').replace(/\D/g, '')
    return digitos.length >= 4 ? `•• •••• ${digitos.slice(-4)}` : 'tu teléfono'
  }

  // Mismo candado que el botón de antes ([disabled]="entity.invalid"): con datos
  // inválidos no se manda nada; en su lugar se marcan los errores en línea.
  enviarCodigo() {
    if (this.entity.invalid) {
      this.entity.markAllAsTouched()
      this.editandoDatos = true
      this.enfocar('.lx-a-entrada .is-invalido input')
      return
    }
    this.sendSMS()
  }

  // Validación de cliente agregada: sin código no se llama a continue().
  verificarCodigo() {
    this.intentoCodigo = true
    if (this.codigoVacio) {
      this.enfocar('#code')
      return
    }
    this.continue()
  }

  // Lleva al primer campo con error (el botón está fijo abajo y el campo puede
  // quedar fuera de la vista). Espera a que se pinten los mensajes.
  private enfocar(selector: string) {
    setTimeout(() => {
      const campo = typeof document !== 'undefined' ? document.querySelector<HTMLElement>(selector) : null
      campo?.focus()
    })
  }

  enviarFormulario() {
    if (!this.codeSent) this.enviarCodigo()
    else if (this.isPhoneVerified) this.continueProcess()
    else this.verificarCodigo()
  }

  cerrarAviso() {
    this.avisoEntrada = null
  }

  private mostrarAviso(m: Message | Message[]) {
    const mensaje = Array.isArray(m) ? m[m.length - 1] : m
    if (!mensaje) return
    const error = mensaje.severity === 'error'
    this.avisoEntrada = {
      tipo: error ? 'error' : 'exito',
      titulo: error ? 'No pudimos continuar' : '¡Listo!',
      texto: (mensaje.detail ?? '').trim() || (error ? 'Inténtalo de nuevo en unos minutos.' : '')
    }
  }

  // Lista: mientras llega POST /flow se ven esqueletos; si falla, un aviso con
  // «Reintentar» (retryFlow, la misma carga de siempre).
  get cargandoPasos(): boolean {
    return this.cargandoFlujo
  }

  private claveFinal = ''
  private finalCache: IEstadoFinal | null = null

  // Mensaje y línea de tiempo de los estados finales (crédito vs retiros). Se
  // guarda por estado para que *ngFor no rehaga la línea en cada ciclo.
  get estadoFinal(): IEstadoFinal | null {
    const status = this.flowStatus?.status ?? ''
    const clave = `${status}|${this.isLoan ? 'L' : 'W'}`
    if (clave !== this.claveFinal) {
      this.claveFinal = clave
      this.finalCache = this.armarEstadoFinal(status, this.isLoan)
    }
    return this.finalCache
  }

  private armarEstadoFinal(status: string, credito: boolean): IEstadoFinal | null {
    const firmado = ['SIGNED', 'PENDING_DISPERSION', 'DISPERSED'].includes(status)
    const paso = (titulo: string, texto: string, estado: EstadoLx, chip = ESTADOS_LX[estado].texto): IPasoLineaFinal =>
      ({titulo, texto, estado, chip})
    const validacion = paso('Validación de documentos', 'Aprobamos tus documentos', 'listo')
    const firma = status === 'PENDING_SIGNATURE'
      ? paso('Firma de contrato', 'Te toca firmar', 'activo')
      : paso('Firma de contrato', 'Firmaste tu contrato', 'listo')
    let ultimo: IPasoLineaFinal
    if (credito) {
      if (status === 'DISPERSED') ultimo = paso('Depósito de tu crédito', 'Depositamos tu crédito', 'listo')
      else if (status === 'PENDING_DISPERSION') ultimo = paso('Depósito de tu crédito', 'El dinero va en camino', 'revision', 'En proceso')
      else if (status === 'SIGNED') ultimo = paso('Depósito de tu crédito', 'Estamos aprobando tu crédito', 'revision', 'En proceso')
      else ultimo = paso('Depósito de tu crédito', 'Después de firmar', 'pendiente')
    } else {
      ultimo = firmado
        ? paso('Retiros activos', 'Ya puedes pedir retiros', 'listo')
        : paso('Retiros activos', 'Después de firmar', 'pendiente')
    }
    const linea = [validacion, firma, ultimo]
    const final = (datos: Omit<IEstadoFinal, 'linea' | 'chipTexto'> & {chipTexto?: string}): IEstadoFinal =>
      ({...datos, chipTexto: datos.chipTexto ?? ESTADOS_LX[datos.chip].texto, linea})

    switch (status) {
      case 'PENDING_SIGNATURE':
        return final({
          exito: false, icono: faFileSignature, chip: 'activo', notaTipo: 'info',
          titulo: 'Tu contrato está listo para firmar',
          texto: credito
            ? 'Revísalo y fírmalo para que podamos depositarte tu crédito.'
            : 'Revísalo y fírmalo para activar tus retiros.',
          nota: 'Busca en tu correo el mensaje con tu contrato o fírmalo desde la aplicación.'
        })
      case 'SIGNED':
      case 'PENDING_DISPERSION':
      case 'DISPERSED':
        if (!credito) {
          return final({
            exito: true, icono: faCheck, chip: 'listo', chipTexto: 'Activo', notaTipo: 'exito',
            titulo: '¡Tus retiros están activos!',
            texto: 'Ya puedes pedir retiros desde la aplicación cuando los necesites.',
            nota: 'Tu contrato quedó firmado y tu servicio está activo.'
          })
        }
        if (status === 'DISPERSED') {
          return final({
            exito: true, icono: faCheck, chip: 'listo', chipTexto: 'Depositado', notaTipo: 'exito',
            titulo: '¡Tu crédito ya está en tu cuenta!',
            texto: 'Depositamos el monto de tu crédito. ¡Felicidades!',
            nota: 'Revisa tu saldo y disfruta tu crédito.'
          })
        }
        if (status === 'PENDING_DISPERSION') {
          return final({
            exito: false, icono: faPaperPlane, chip: 'revision', chipTexto: 'En proceso', notaTipo: 'info',
            titulo: 'Estamos depositando tu crédito',
            texto: 'Tu crédito ya está aprobado y el dinero va en camino a tu cuenta.',
            nota: 'Te avisamos cuando el depósito esté hecho.'
          })
        }
        return final({
          exito: false, icono: faHourglassHalf, chip: 'revision', chipTexto: 'En proceso', notaTipo: 'info',
          titulo: '¡Firmaste tu contrato!',
          texto: 'Estamos aprobando tu crédito. Te avisamos cuando lo depositemos.',
          nota: 'No tienes que hacer nada más por ahora.'
        })
      default:
        return null
    }
  }


  closeSession() {
    this.authService.logout()
    this.flujoCargado = false
    this.stepProcess = 'RFC'
  }
  startTimer() {
    this.clearTimer(); // Asegura que no haya otro timer en marcha
    this.timer = 120;
    console.log("start")
    this.timerInterval = setInterval(() => {
      if (this.timer > 0) {
        this.timer--; // Decrementa en 1 cada segundo
      } else {
        this.clearTimer(); // Limpia el intervalo cuando llega a 0
      }
    }, 1000);
  }

  clearTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  getFormattedTimer(): string {
    const minutes = Math.floor(this.timer / 60);
    const seconds = this.timer % 60;
    return `${this.padTime(minutes)}:${this.padTime(seconds)}`;
  }

  private padTime(time: number): string {
    return time < 10 ? `0${time}` : `${time}`;
  }

  continueProcess() {
    this.authService.updateUserLocalData({token: this.token})
    this.stepProcess = 'PROCESS'
    this.getFlow()
  }

  startNewProcess() {
    this.isPhoneVerified = false;
    this.authService.logout()
    this.entity.reset();
    this.codeSent = false;
    this.timer = 0;
    this.flujoCargado = false
    this.stepProcess = 'RFC';
  }

  getQueryParams() {
    console.log("here",this.authService.getToken)
    this.activatedRoute.queryParams.subscribe(params => {
      const session = params['session']; // Cambia 'exampleParam' por el nombre de tu parámetro.
      if (session && this.verifyInformationService.id !== session) {
        this.authService.logout()
      }
      if (this.authService.getToken !== '') {
        this.stepProcess = 'PROCESS'
        this.getFlow()
      } else {
        if (session || this.authService.getToken !== '') {
          this.session = session
          this.expressLeagueService.getExpressLeague({session}).subscribe({
            next: r => {
              console.log(r)
              if (r.data) {
                this.expressLeagueType = r.data.type || 'LOAN'
                this.authService.updateUserLocalData(r.data)
                this.router.navigate([], {queryParams: {}})
                this.stepProcess = 'PROCESS'
                this.getFlow()
              } else {
                this.stepProcess = 'RFC'
              }
            },
            error: () => this.stepProcess = 'RFC'
          })
        } else {
          this.stepProcess = 'RFC'
        }
      }
    });
  }

  sendSMS() {
    this.loaderService.showSpinner()
    this.expressLeagueService.sendSms(this.entity.value).subscribe(r => {
      this.messageService.add({ severity: r.error ? 'error': 'success', summary: r.error ? 'Error': 'Exitoso', detail: r.msg, life: 4000});
      if (!r.error) {
        this.codeSent = true
        this.startTimer()
      }
      this.loaderService.hideSpinner()
    })
  }

  continue() {
    console.log(this.authService.getToken, "tooooken")
    if (this.authService.getToken !== '') {
      this.stepProcess = 'PROCESS'
      this.getFlow()
    } else {
      this.loaderService.showSpinner()
      this.expressLeagueService.createExpressLeague({...this.entity.value, type: this.expressLeagueType}).subscribe(r => {
        if (!r.error) {
          if (r.data && r.data.token) {
            this.authService.updateUserLocalData({token: r.data.token})
            this.stepProcess = 'PROCESS'
            this.getFlow()
          } else {
            this.messageService.add({ severity: 'success', summary: 'Exitoso', detail: r.msg, life: 10000});
          }
        } else {
          this.messageService.add({ severity: r.error ? 'error': 'success', summary: r.error ? 'Error': 'Exitoso', detail: r.msg, life: 4000});
        }
        this.loaderService.hideSpinner()
      })
    }
  }


  // Antes cada carga mandaba POST /flow y dos POST /flow/status A LA VEZ (el otro
  // desde app-progress-verification). El backend abre la solicitud en cualquiera
  // de las tres si no existe, así que llegaban juntas sin encontrarla y abrían tres
  // (ternas en QA del 28 y 30-ago-2026). Ahora van en serie, una carga a la vez;
  // si piden otra mientras hay una en vuelo, se repite al terminar para no quedarse
  // con datos de antes (p. ej. justo después de firmar).
  private cargandoFlujo = false
  private recargarFlujo = false

  getFlow() {
    if (this.cargandoFlujo) {
      this.recargarFlujo = true
      return
    }
    this.cargandoFlujo = true
    this.protegida(this.flowService.getFlow()).pipe(
      concatMap(rFlow => this.protegida(this.flowService.getStatus()).pipe(map(rStatus => ({rFlow, rStatus})))),
      finalize(() => this.terminarCarga())
    ).subscribe(({rFlow, rStatus}) => {
      // Se asignan juntas: si `data` llegara antes que `flowStatus`, la lista se
      // pintaría sin estado ni pushingId mientras responde /flow/status.
      if (!rFlow.error) this.data = rFlow.data
      this.aplicarEstado(rStatus)
    })
  }

  // Al volver a la lista sin pedir recarga completa, solo se refresca el estado
  // (porcentaje y título del círculo). Antes lo hacía progress-verification al
  // montarse, con su propia llamada simultánea.
  private refrescarEstado() {
    if (this.cargandoFlujo) {
      this.recargarFlujo = true
      return
    }
    this.cargandoFlujo = true
    this.protegida(this.flowService.getStatus()).pipe(
      finalize(() => this.terminarCarga())
    ).subscribe(r => this.aplicarEstado(r, false))
  }

  private terminarCarga() {
    this.cargandoFlujo = false
    this.flujoCargado = true
    if (this.recargarFlujo) {
      this.recargarFlujo = false
      this.getFlow()
    }
  }

  private aplicarEstado(r: {error: boolean; data: IFlowStatus}, actualizarTitulo = true) {
    if (r.error || !r.data) return
    this.flowStatus = r.data
    // Si el usuario ya entró a una etapa, no se le pisa el título de esa pantalla.
    if (actualizarTitulo && this.screenVerificationList) this.titleSection = r.data.flow.name
  }

  // Una petición colgada no debe dejar la pantalla sin recargas para siempre, y un
  // error de red sin cuerpo no debe cortar la cadena antes de pedir el estado.
  private protegida<T extends {error: boolean}>(peticion: Observable<T>): Observable<T> {
    return peticion.pipe(
      timeout(30000),
      catchError(() => of({error: true, msg: 'Hubo un error de conexión', data: null} as unknown as T))
    )
  }

  retryFlow() {
    this.getFlow()
  }

  showScreen(stage: IFlow) {
    console.log(stage)
    console.log("data", this.data)
    this.screenVerificationList = false
    this.backRoute = '/verify-documents'
    this.titleSection = stage.properties.title
    this.currentScreenType = stage.properties.type
    this.currentScreenData = stage
  }

  isStatusBeyondDocuments(): boolean {
    // IN_VERIFICATION no está aquí a propósito: un crédito puede existir aunque aún falten
    // documentos, así que ese estado sigue mostrando la lista de pasos (con «Ya no te
    // falta nada» cuando ya está todo), no una pantalla de revisión.
    const advancedStatuses = ['PENDING_SIGNATURE', 'SIGNED', 'PENDING_DISPERSION', 'DISPERSED'];
    return !!this.flowStatus && advancedStatuses.includes(this.flowStatus.status);
  }

  get isWithdrawal(): boolean {
    return !this.isLoan;
  }

  // El backend manda productType porque hay muchos flujos derivados de crédito; el
  // identifier solo se usa como respaldo si responde un backend sin ese campo.
  get isLoan(): boolean {
    const productType = this.flowStatus?.productType
    if (productType) return productType === 'LOAN'
    return this.flowStatus?.flow?.identifier === 'PULPI_NEW_LOAN_WITHOUT_SALARY';
  }

  showVerificationScreen (retryFlow: false) {
    this.screenVerificationList = true
    this.backRoute = '/'
    // Mismo título de banda que al entrar (el nombre del flujo), sin el parpadeo
    // de un texto distinto mientras llega /flow/status.
    this.titleSection = this.flowStatus?.flow?.name || 'Tu solicitud'
    //if(window['ReactNativeWebView' as unknown as any]) window['ReactNativeWebView' as unknown as any].postMessage(JSON.stringify({type: 'CLOSE'}))
    if (retryFlow) this.getFlow()
    else this.refrescarEstado()
  }
}
